import { ThemeModule } from '../../front/lib/ThemeModule';
import { addLayoutResizeListener, removeLayoutResizeListener } from '../../front/lib/utils';
export class Card extends ThemeModule {
    constructor() {
        super(...arguments);
        this.articleTextBodyEl = null;
    }
    init() {
        if (this.root.classList.contains('appearance--article')) {
            this.initArticleCard();
        }
        Card.initStatic();
        this.addCleanupHandler(() => Card.articleCards = Card.articleCards.filter(card => card !== this));
    }
    static initStatic() {
        if (this.staticReady) {
            return;
        }
        window.addEventListener('load', () => this.updateAricleRows());
        window.addEventListener('orientationchange', () => this.updateAricleRows());
        window.addEventListener('resize', () => this.updateAricleRows());
        this.staticReady = true;
    }
    /**
     * Split cards into rows, based on visual placement, i.e. top position.
     * This allows to infer the context of a card, e.g. if it is the only card in a row,
     * or if at least one other card in the row has an image etc.
     */
    static updateAricleRows() {
        const rows = {};
        for (const card of Card.articleCards) {
            const top = card.root.getBoundingClientRect().top;
            if (!rows[top]) {
                rows[top] = [];
            }
            rows[top].push(card);
            card.articleCardAdjustTextHeight();
        }
        let withRowImageStreak = 0;
        for (const row of Object.values(rows)) {
            if (row.length === 1) {
                row[0].root.classList.add('rowCtx--solitary');
            }
            const hasImage = row.some(card => card.root.classList.contains('with--image'));
            if (hasImage) {
                withRowImageStreak++;
                const alterate = withRowImageStreak % 2 === 0;
                for (const card of row) {
                    card.root.classList.add('rowCtx--withImage');
                    if (alterate) {
                        card.root.classList.add('rowCtx--withImage--alt');
                    }
                }
            }
            else {
                withRowImageStreak = 0;
            }
        }
        // Update text height after image height has been set
        for (const card of Card.articleCards) {
            card.articleCardAdjustTextHeight();
        }
    }
    articleCardAdjustTextHeight() {
        if (this.articleTextBodyEl) {
            const computedStyle = window.getComputedStyle(this.articleTextBodyEl);
            const lineHeight = computedStyle.lineHeight.substring(0, computedStyle.lineHeight.length - 2);
            const inRowWithImage = !this.root.classList.contains('with--image') && this.root.classList.contains('rowCtx--withImage');
            let numLines = Math.floor(this.articleTextBodyEl.clientHeight / parseInt(lineHeight, 10));
            if (inRowWithImage) {
                numLines += 2;
            }
            this.articleTextBodyEl.style.setProperty('--num-lines', numLines.toString());
        }
    }
    initArticleCard() {
        Card.articleCards.push(this);
        this.articleTextBodyEl = this.getElementById('body');
        this.initArticleFullView();
        this.initArticleTerms();
    }
    initArticleTerms() {
        const primaryEl = this.getElementById('primary');
        const termsBtnEl = this.getElementById('termsBtn');
        const termsCloseBtnEl = this.getElementById('termsCloseBtn');
        const termsEl = this.getElementById('terms');
        const isTermsVisible = () => {
            return termsEl === null || termsEl === void 0 ? void 0 : termsEl.classList.contains('is--visible');
        };
        const showTerms = () => {
            termsEl === null || termsEl === void 0 ? void 0 : termsEl.classList.add('is--visible');
            termsEl === null || termsEl === void 0 ? void 0 : termsEl.removeAttribute('inert');
            primaryEl === null || primaryEl === void 0 ? void 0 : primaryEl.setAttribute('inert', 'inert');
            termsBtnEl === null || termsBtnEl === void 0 ? void 0 : termsBtnEl.setAttribute('aria-expanded', 'true');
            const clickListener = (event) => {
                if (!this.root.contains(event.target)) {
                    hideTerms();
                    cleanup();
                }
            };
            const cleanup = this.addCleanupHandler(() => document.body.removeEventListener('click', clickListener));
            document.body.addEventListener('click', clickListener);
        };
        const hideTerms = () => {
            termsEl === null || termsEl === void 0 ? void 0 : termsEl.classList.remove('is--visible');
            termsEl === null || termsEl === void 0 ? void 0 : termsEl.setAttribute('inert', 'inert');
            primaryEl === null || primaryEl === void 0 ? void 0 : primaryEl.removeAttribute('inert');
            termsBtnEl === null || termsBtnEl === void 0 ? void 0 : termsBtnEl.setAttribute('aria-expanded', 'false');
        };
        const toggleTerms = () => {
            isTermsVisible() ? hideTerms() : showTerms();
        };
        termsBtnEl === null || termsBtnEl === void 0 ? void 0 : termsBtnEl.addEventListener('click', toggleTerms);
        termsCloseBtnEl === null || termsCloseBtnEl === void 0 ? void 0 : termsCloseBtnEl.addEventListener('click', hideTerms);
    }
    initArticleFullView() {
        const moreBtnEl = this.getElementById('moreBtn');
        if (!moreBtnEl) {
            return;
        }
        const innerEl = this.getElementById('inner');
        const bodyInnerEl = this.getElementById('body-inner');
        // Placeholder element to keep the layout stable when the full view is shown
        let placeholderEl = null;
        let parentModulesEls = [];
        const showFullView = () => {
            var _a, _b;
            placeholderEl = innerEl.cloneNode(true);
            placeholderEl.classList.add('is--placeholder');
            placeholderEl.setAttribute('inert', 'inert');
            innerEl.classList.add('is--primaryEl');
            (_a = innerEl.parentElement) === null || _a === void 0 ? void 0 : _a.appendChild(placeholderEl);
            // Add depth priority to all parent modules
            // To ensure that they are visible above any other elements
            let parent = this.root;
            while (parent !== document.body) {
                if ((_b = parent === null || parent === void 0 ? void 0 : parent.dataset) === null || _b === void 0 ? void 0 : _b.tmod) {
                    parent.classList.add('depthPrio--high');
                    parentModulesEls.push(parent);
                }
                parent = parent === null || parent === void 0 ? void 0 : parent.parentElement;
            }
            // Handle click outside
            const clickListener = (event) => {
                if (!this.root.contains(event.target)) {
                    hideFullView();
                    cleanup();
                }
            };
            const cleanup = this.addCleanupHandler(() => {
                document.body.removeEventListener('click', clickListener);
                window.removeEventListener('beforeunload', hideFullView);
            });
            document.body.addEventListener('click', clickListener, { capture: true });
            window.addEventListener('beforeunload', hideFullView);
        };
        const hideFullView = () => {
            var _a;
            (_a = placeholderEl === null || placeholderEl === void 0 ? void 0 : placeholderEl.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(placeholderEl);
            placeholderEl = null;
            innerEl.classList.remove('is--primaryEl');
            parentModulesEls.forEach(m => m.classList.remove('depthPrio--high'));
        };
        const isFullView = () => {
            return !!placeholderEl;
        };
        const toggleFullView = () => {
            isFullView() ? hideFullView() : showFullView();
        };
        moreBtnEl.addEventListener('click', toggleFullView);
        const onArticleCardResize = () => {
            const fullView = isFullView();
            if (bodyInnerEl.scrollHeight > bodyInnerEl.clientHeight) {
                if (fullView) {
                    hideFullView();
                }
                this.root.classList.add('has--moreBtn');
                moreBtnEl.classList.remove('is--hidden');
            }
            else if (!fullView) {
                this.root.classList.remove('has--moreBtn');
                moreBtnEl.classList.add('is--hidden');
            }
        };
        onArticleCardResize();
        const updateIntval = setInterval(onArticleCardResize, 2000);
        this.addCleanupHandler(() => {
            clearInterval(updateIntval);
            removeLayoutResizeListener(onArticleCardResize);
        });
        addLayoutResizeListener(onArticleCardResize);
    }
}
Card.articleCards = [];
Card.staticReady = false;
